import type { OptionsBase } from "../types";

type Range = [number, number];

// East Asian Width: Wide(W) / Fullwidth(F)
const wideRanges: Range[] = [
	[0x1100, 0x115f],
	[0x2e80, 0x303e],
	[0x3041, 0x33ff],
	[0x3400, 0x4dbf],
	[0x4e00, 0x9fff],
	[0xa000, 0xa4cf],
	[0xa960, 0xa97f],
	[0xac00, 0xd7a3],
	[0xf900, 0xfaff],
	[0xfe10, 0xfe19],
	[0xfe30, 0xfe6f],
	[0xff00, 0xff60],
	[0xffe0, 0xffe6],
	[0x1f300, 0x1f64f],
	[0x1f900, 0x1f9ff],
	[0x20000, 0x3fffd],
];

// East Asian Width: Ambiguous(A)
// 罫線素片(0x2500-0x254b)もここに含まれる
const ambiguousRanges: Range[] = [
	[0x00a1, 0x00a1],
	[0x00a4, 0x00a4],
	[0x00a7, 0x00a8],
	[0x00aa, 0x00aa],
	[0x00ad, 0x00ae],
	[0x00b0, 0x00b4],
	[0x00b6, 0x00ba],
	[0x00bc, 0x00bf],
	[0x00c6, 0x00c6],
	[0x00d0, 0x00d0],
	[0x00d7, 0x00d8],
	[0x00de, 0x00e1],
	[0x00e6, 0x00e6],
	[0x00e8, 0x00ea],
	[0x00ec, 0x00ed],
	[0x00f0, 0x00f0],
	[0x00f2, 0x00f3],
	[0x00f7, 0x00fa],
	[0x00fc, 0x00fc],
	[0x00fe, 0x00fe],
	[0x2010, 0x2010],
	[0x2013, 0x2016],
	[0x2018, 0x2019],
	[0x201c, 0x201d],
	[0x2020, 0x2022],
	[0x2024, 0x2027],
	[0x2030, 0x2030],
	[0x2032, 0x2033],
	[0x2035, 0x2035],
	[0x203b, 0x203b],
	[0x203e, 0x203e],
	[0x2074, 0x2074],
	[0x207f, 0x207f],
	[0x2081, 0x2084],
	[0x20ac, 0x20ac],
	[0x2103, 0x2103],
	[0x2105, 0x2105],
	[0x2109, 0x2109],
	[0x2113, 0x2113],
	[0x2116, 0x2116],
	[0x2121, 0x2122],
	[0x2126, 0x2126],
	[0x212b, 0x212b],
	[0x2153, 0x2154],
	[0x215b, 0x215e],
	[0x2160, 0x216b],
	[0x2170, 0x2179],
	[0x2189, 0x2189],
	[0x2190, 0x2199],
	[0x21b8, 0x21b9],
	[0x21d2, 0x21d2],
	[0x21d4, 0x21d4],
	[0x21e7, 0x21e7],
	[0x2200, 0x2200],
	[0x2202, 0x2203],
	[0x2207, 0x2208],
	[0x220b, 0x220b],
	[0x220f, 0x220f],
	[0x2211, 0x2211],
	[0x2215, 0x2215],
	[0x221a, 0x221a],
	[0x221d, 0x2220],
	[0x2223, 0x2223],
	[0x2225, 0x2225],
	[0x2227, 0x222c],
	[0x222e, 0x222e],
	[0x2234, 0x2237],
	[0x223c, 0x223d],
	[0x2248, 0x2248],
	[0x224c, 0x224c],
	[0x2252, 0x2252],
	[0x2260, 0x2261],
	[0x2264, 0x2267],
	[0x226a, 0x226b],
	[0x226e, 0x226f],
	[0x2282, 0x2283],
	[0x2286, 0x2287],
	[0x2295, 0x2295],
	[0x2299, 0x2299],
	[0x22a5, 0x22a5],
	[0x22bf, 0x22bf],
	[0x2312, 0x2312],
	[0x2460, 0x24e9],
	[0x24eb, 0x254b],
	[0x2550, 0x2573],
	[0x2580, 0x258f],
	[0x2592, 0x2595],
	[0x25a0, 0x25a1],
	[0x25a3, 0x25a9],
	[0x25b2, 0x25b3],
	[0x25b6, 0x25b7],
	[0x25bc, 0x25bd],
	[0x25c0, 0x25c1],
	[0x25c6, 0x25c8],
	[0x25cb, 0x25cb],
	[0x25ce, 0x25d1],
	[0x25e2, 0x25e5],
	[0x25ef, 0x25ef],
	[0x2605, 0x2606],
	[0x2609, 0x2609],
	[0x260e, 0x260f],
	[0x2614, 0x2615],
	[0x261c, 0x261c],
	[0x261e, 0x261e],
	[0x2640, 0x2640],
	[0x2642, 0x2642],
	[0x2660, 0x2661],
	[0x2663, 0x2665],
	[0x2667, 0x266a],
	[0x266c, 0x266d],
	[0x266f, 0x266f],
	[0x273d, 0x273d],
	[0x2776, 0x277f],
	[0xe000, 0xf8ff],
	[0xfffd, 0xfffd],
];

const includes = (ranges: Range[], code: number): boolean => {
	let low = 0;
	let high = ranges.length - 1;

	while (low <= high) {
		const middle = Math.floor((low + high) / 2);
		const [start, end] = ranges[middle];
		if (code < start) high = middle - 1;
		else if (code > end) low = middle + 1;
		else return true;
	}

	return false;
};

/** East Asian Width が Ambiguous の文字を何幅として数えるか。 */
type AmbiguousWidth = 1 | 2;

const charWidth = (code: number, ambiguousWidth: AmbiguousWidth): number => {
	if (code === 0) return 0;
	if (code < 0x20 || (code >= 0x7f && code < 0xa0)) return 0; // 制御文字
	if (code < 0x7f) return 1;
	if (code >= 0x0300 && code <= 0x036f) return 0; // 結合文字
	if (code === 0x200b || code === 0xfe0f) return 0; // ゼロ幅・異体字セレクタ
	if (includes(wideRanges, code)) return 2;
	if (includes(ambiguousRanges, code)) return ambiguousWidth;
	return 1;
};

/**
 * 等幅表示を前提とした表示幅を返す。
 * 罫線素片や全角スペースを混在させるため、East Asian Width を見て 1/2 を数える。
 */
const stringWidth = (text: string, ambiguousWidth: AmbiguousWidth): number => {
	let width = 0;
	for (const char of text) {
		width += charWidth(char.codePointAt(0) as number, ambiguousWidth);
	}
	return width;
};

/**
 * CJKフォント前提かどうかを、Ambiguousな文字の幅に読み替える。
 * CJKフォントでは罫線素片などが全角で描画されるため2幅として数える。
 */
const ambiguousWidthOf = (cjkFont: OptionsBase["cjkFont"]): AmbiguousWidth => {
	return cjkFont ? 2 : 1;
};

/**
 * markdown記法のうち、レンダリング後に消える文字を取り除く。
 * keepMarkdown:true のとき、`**bold**` を4文字ではなく8文字と数えてしまうのを防ぐ。
 *
 * あくまで文字数の話であり、太字や色などレンダリング後の字形の違いまでは扱えない。
 */
const stripMarkdown = (text: string): string => {
	return (
		text
			// 画像・リンク: ![alt](src) / [label](href) -> alt / label
			.replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
			// 参照リンク: [label][ref] -> label
			.replace(/!?\[([^\]]*)\]\[[^\]]*\]/g, "$1")
			// コードスパン: `code` -> code
			.replace(/`+([^`]*)`+/g, "$1")
			// 強調: **bold** / *it* / __b__ / _i_ -> 中身
			// 打ち消し(~~)はGFM拡張でパーサが解釈しないため、ここでも除かない
			.replace(/(\*\*\*|\*\*|\*|___|__|_)(.+?)\1/g, "$2")
			// エスケープ: \* -> *
			.replace(/\\([!-/:-@[-`{-~])/g, "$1")
	);
};

export { ambiguousWidthOf, stringWidth, stripMarkdown };
